import { Key } from "react";

import axios from "axios";
import snakeCase from "lodash/snakeCase";

import { queryCache } from "react-query";

import { GET_DEAL_KEY } from "manager/hooks/api";

import ROUTES from "shared/config/apiRoutes";
import { transformRoute } from "shared/utils/routing";

import { API_URL } from "./setupApi";
import { notify } from "./utils";

const cancelDeclineApiCall = (
  route,
  id,
  attachments,
  extraParams,
  sendEmail?: string,
  emailBody?: string
) => {
  const formData = new FormData();
  if (sendEmail) {
    formData.set("send_email", sendEmail);
  }
  if (emailBody) {
    formData.set("email_body", emailBody);
  }
  Object.keys(extraParams || {}).forEach((extraParam) => {
    formData.set(snakeCase(extraParam), extraParams[extraParam]);
  });
  (attachments || []).forEach((att, idx) => {
    formData.set(`file${idx + 1}`, att);
  });
  return axios.post(`${API_URL}${transformRoute(route, { id })}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

const approveApiCall = (route, id, attachments, extraParams) => {
  const formData = new FormData();
  Object.keys(extraParams || {}).forEach((extraParam) => {
    formData.set(snakeCase(extraParam), extraParams[extraParam]);
  });
  (attachments || []).forEach((att, idx) => {
    formData.set(`file${idx + 1}`, att);
  });
  return axios.post(`${API_URL}${transformRoute(route, { id })}`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const inviteApplicants = async ({
  applicants,
  unitListing,
  unit,
  noListingUnit,
  leadSource,
  externalPMSId,
}) =>
  axios.post(`${API_URL}${ROUTES.inviteApplicant}`, {
    applicants,
    unitListing,
    unit,
    noListingUnit,
    leadSource,
    externalPMSId,
  });

export const applicantInviteApplicants = async ({
  applicants,
  otherOccupants,
  dealId,
}) =>
  axios.post(
    `${API_URL}${transformRoute(ROUTES.applicantInviteApplicants, {
      id: dealId,
    })}`,
    {
      applicants,
      otherOccupants,
    }
  );

export type RemoveApplicantFromDealProps = {
  applicationId: string | number;
  id: string;
  comment?: string;
  reason?: number;
};

export const removeApplicationFromDeal = async ({
  applicationId,
  id,
  comment,
  reason,
}: RemoveApplicantFromDealProps) =>
  notify(
    () =>
      axios.post(
        `${API_URL}${transformRoute(ROUTES.removeApplicationFromDeal, {
          id,
        })}`,
        {
          application: applicationId,
          comment,
          reason,
        }
      ),
    "Successfully removed applicant",
    "Failed to remove applicant"
  );

export const inviteApplicantsToDeal = async ({ applicants, id }) =>
  axios.post(`${API_URL}${ROUTES.inviteApplicantsToDeal}`, {
    applicants,
    deal: id,
  });

export const getApplicantDeal = async (id) =>
  axios.get(`${API_URL}${transformRoute(ROUTES.applicantDeal, { id })}`);

export const getDeal = async (id) =>
  axios.get(`${API_URL}${transformRoute(ROUTES.deal, { id })}`);

export const generateDealPDF = async (id) =>
  axios
    .post(
      `${API_URL}${transformRoute(ROUTES.generateDealPDF, {
        id,
      })}`
    )
    .then((response) => response.data);

export const cancelDeal = async ({
  id,
  sendEmail,
  emailBody,
  attachments,
  extraParams,
}) => {
  return notify(
    () =>
      cancelDeclineApiCall(
        ROUTES.cancelDeal,
        id,
        attachments,
        extraParams,
        sendEmail,
        emailBody
      ),
    "Successfully canceled",
    "Failed to cancel"
  );
};
export const approveDeal = async ({ id, attachments, extraParams }) => {
  return notify(
    async () => {
      const data = await approveApiCall(
        ROUTES.approveDeal,
        id,
        attachments,
        extraParams
      );
      queryCache.invalidateQueries(GET_DEAL_KEY);
      return data;
    },
    "Successfully approved",
    "Failed to approve"
  );
};

export const declineDeal = async ({ id, attachments, extraParams }) => {
  return notify(
    () =>
      cancelDeclineApiCall(ROUTES.declineDeal, id, attachments, extraParams),
    "Successfully declined",
    "Failed to decline"
  );
};

/**
 * Helper function to handle FormData construction and API call for conditionally approving a deal with attachments
 *
 * @param {any} data - JSON data containing conditional approval information
 * @param {string|number} id - The deal ID
 * @param {File[]} attachments - Array of file attachments
 * @returns {Promise} - Axios request promise
 */
const conditionallyApproveDealWithAttachmentsCall = (data, id, attachments) => {
  const formData = new FormData();

  // Add JSON data as a string in the 'data' field
  formData.set("data", JSON.stringify(data));

  // Add each file to the formData
  (attachments || []).forEach((file, index) => {
    formData.set(`file${index + 1}`, file);
  });

  return axios.post(
    `${API_URL}${transformRoute(
      ROUTES.conditionallyApproveDealWithAttachments,
      { id }
    )}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
};

/**
 * Conditionally approve a deal with file attachments, using multipart/form-data
 *
 * @param {Object} params - Parameters object
 * @param {any} params.data - JSON data containing conditional approval information
 * @param {string|number} params.id - The deal ID
 * @param {File[]} params.attachments - Array of file attachments
 * @returns {Promise} - Promise with notification handling
 */
export const conditionallyApproveDealWithAttachments = async ({
  data,
  id,
  attachments,
}) => {
  return notify(
    () => conditionallyApproveDealWithAttachmentsCall(data, id, attachments),
    "Successfully requested more info",
    "Failed to request more info"
  );
};

/**
 * Conditionally approve a deal, with or without attachments.
 * This function will use the appropriate endpoint based on whether attachments are present.
 *
 * @param {Object} params - Parameters object
 * @param {any} params.data - JSON data containing conditional approval information
 * @param {string|number} params.id - The deal ID
 * @param {File[]} [params.attachments] - Optional array of file attachments
 * @returns {Promise} - Promise with notification handling
 */
export const conditionallyApproveDeal = async ({ data, id, attachments }) => {
  // If attachments are present, use the attachment-specific function
  if (attachments && attachments.length > 0) {
    return conditionallyApproveDealWithAttachments({ data, id, attachments });
  }

  // Use the legacy endpoint for requests without attachments
  return notify(
    () =>
      axios.post(
        `${API_URL}${transformRoute(ROUTES.conditionallyApproveDeal, {
          id,
        })}`,
        data
      ),
    "Successfully requested more info",
    "Failed to request more info"
  );
};

export const previewConditionalApprovalEmail = async ({ data, id }) => {
  return axios.post(
    `${API_URL}${transformRoute(ROUTES.previewConditionalApprovalEmail, {
      id,
    })}`,
    data
  );
};

export const conditionallyProceed = async ({ id }) => {
  return axios.post(
    `${API_URL}${transformRoute(ROUTES.conditionallyProceed, { id })}`
  );
};

export const sendDealToPMS = async ({ id }) => {
  return notify(async () =>
    axios.post(
      `${API_URL}${transformRoute(ROUTES.sendToPms, {
        id,
      })}`
    )
  );
};

export const resendToPms = async ({ id }) => {
  return notify(async () =>
    axios.post(
      `${API_URL}${transformRoute(ROUTES.resendToPms, {
        id,
      })}`
    )
  );
};

export const getManager = ({ id }) =>
  axios
    .get(`${API_URL}${transformRoute(ROUTES.contactManger, { id })}`)
    .then(({ data }) => data);

export const contactManager = ({
  id,
  applications,
  otherOccupants,
  additionalMessage,
}) =>
  axios.post(`${API_URL}${transformRoute(ROUTES.contactManger, { id })}`, {
    applications,
    otherOccupants,
    additionalMessage,
  });

export const getDeals = ({
  dateMin,
  dateMax,
  propertyId,
  unitPropertyOrApplicant,
  dealStatus,
  leaseStatus,
  pageSize,
  page,
  ordering,
}) =>
  axios.get(`${API_URL}${ROUTES.deals}`, {
    params: {
      created_at__gte: dateMin,
      created_at__lte: dateMax,
      property_id__in: propertyId,
      unit_or_property_or_applicant: unitPropertyOrApplicant,
      deal_status: dealStatus,
      lease_status: leaseStatus,
      page_size: pageSize,
      page,
      ordering,
    },
  });
export const editDealRent = ({ dealId, rent }) =>
  axios.post(
    `${API_URL}${transformRoute(ROUTES.editDealRent, {
      id: dealId,
    })}`,
    { rent }
  );

export const assignDealToUnit = ({
  id,
  unitId,
  newLeaseStartDate,
  extraParams,
}) =>
  axios
    .post(`${API_URL}${transformRoute(ROUTES.assignDealToUnit, { id })}`, {
      unit: unitId,
      new_preferred_lease_start_date: newLeaseStartDate,
      ...extraParams,
    })
    .then(({ data }) => data);

export const getUnitListing = (id: Key) =>
  axios
    .get(`${API_URL}${transformRoute(ROUTES.dealUnitListing, { id })}`)
    .then(({ data }) => data);

export const getPrimaryApplicant = (id: Key) =>
  axios
    .get(`${API_URL}${transformRoute(ROUTES.dealApplications, { id })}`, {
      params: {
        position: "primary",
      },
    })
    .then(({ data }) => data);

export const getDealApplicationsSummary = (
  id: Key,
  options: { mode?: "deal" } = {}
) =>
  axios
    .get(
      `${API_URL}${transformRoute(ROUTES.dealApplicationsSummary, { id })}`,
      options.mode ? { params: { mode: options.mode } } : undefined
    )
    .then(({ data }) => data);

export const updateDealRevenueDetails = ({ dealId, values }) =>
  axios.patch(
    `${API_URL}${transformRoute(ROUTES.updateDealRevenueDetails, {
      id: dealId,
    })}`,
    values
  );

export const assignDealRevenueManagement = ({ dealId, values }) =>
  axios.patch(
    `${API_URL}${transformRoute(ROUTES.assignDealRevenueManagement, {
      id: dealId,
    })}`,
    values
  );

/**
 * Default chunk size for splitting applicationIds in API calls
 */
const DEFAULT_APPLICATION_IDS_CHUNK_SIZE = 3;

/**
 * Splits an array into chunks of specified size
 */
const chunkArray = <T>(array: T[], chunkSize: number): T[][] => {
  if (chunkSize <= 0) return [array];
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    chunks.push(array.slice(i, i + chunkSize));
  }
  return chunks;
};

/**
 * Fetches deal applicants, splitting applicationIds into chunks and merging results
 *
 * @param id - Deal ID
 * @param applicationIds - Array of application IDs to fetch
 * @param chunkSize - Number of applicationIds per API call (default: 10)
 * @returns Merged array of applicant data from all chunks
 */
export const getDealApplicants = async (
  id: Key,
  applicationIds?: number[],
  chunkSize: number = DEFAULT_APPLICATION_IDS_CHUNK_SIZE
) => {
  const url = `${API_URL}${transformRoute(ROUTES.dealApplications, { id })}`;
  console.log(applicationIds, "applicationIds");

  // If no applicationIds provided, make a single call without filtering
  if (applicationIds.length === 0 || applicationIds.length < 5) {
    const { data } = await axios.get(url);
    return data;
  }

  // Split applicationIds into chunks
  const chunks = chunkArray(applicationIds, chunkSize);

  // Make parallel API calls for each chunk
  const responses = await Promise.all(
    chunks.map((idsChunk) =>
      axios.get(url, {
        params: {
          application_ids: idsChunk.join(","),
        },
      })
    )
  );

  // Merge all response data arrays
  const mergedData = responses.flatMap((response) => response.data);

  return mergedData;
};
